import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import PhoneInput from "@/components/PhoneInput";
import { HotelNavPills } from "@/pages/HotelDashboard";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  MapPin,
  Plus,
  Trash2,
  Sparkles,
  ArrowDown,
  Flag,
  PlayCircle,
  Clock,
  Route,
  Users,
  Briefcase,
  Check,
} from "lucide-react";

const MAX_STOPS = 5;
const emptyWaypoint = () => ({ address: "", coord: null });

/**
 * Multi-destination booking page (iteration 77).
 * L'hôtel saisit manuellement le départ + l'arrivée + jusqu'à 5 arrêts
 * intermédiaires. Le prix est calculé via Mapbox Directions sur toute la
 * polyline (départ → arrêts → arrivée).
 */
export default function HotelMultiDestination() {
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [pickup, setPickup] = useState(emptyWaypoint());
  const [dropoff, setDropoff] = useState(emptyWaypoint());
  const [stops, setStops] = useState([]); // intermediate stops
  const [client, setClient] = useState({
    client_name: "",
    client_phone: "",
    room_number: "",
    passenger_count: 1,
    luggage_count: 0,
    notes: "",
  });
  const [vehicleType, setVehicleType] = useState("sedan");
  const [bookingType, setBookingType] = useState("immediate");
  const [scheduledAt, setScheduledAt] = useState("");
  const [receptionistId, setReceptionistId] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    api.get("/hotel/profile").then((r) => {
      setProfile(r.data);
      // Pré-remplir le départ avec l'adresse de l'hôtel par défaut
      // (modifiable par l'utilisateur).
      if (r.data?.hotel_address) {
        setPickup({
          address: r.data.hotel_address,
          coord: r.data?.coord || null,
        });
      }
    });
  }, []);

  const receptionists = profile?.receptionists || [];

  const addStop = () => {
    if (stops.length >= MAX_STOPS) {
      toast.error(`${MAX_STOPS} arrêts maximum.`);
      return;
    }
    setStops((s) => [...s, emptyWaypoint()]);
  };

  const removeStop = (idx) =>
    setStops((s) => s.filter((_, i) => i !== idx));

  const updateStop = (idx, patch) =>
    setStops((s) => s.map((w, i) => (i === idx ? { ...w, ...patch } : w)));

  const submit = async (e) => {
    e.preventDefault();
    if (!pickup.address.trim()) return toast.error("Adresse de départ requise.");
    if (!dropoff.address.trim()) return toast.error("Adresse d'arrivée requise.");
    for (const [i, s] of stops.entries()) {
      if (!s.address.trim())
        return toast.error(`Adresse de l'arrêt ${i + 1} manquante.`);
    }
    if (!client.client_name.trim()) return toast.error("Nom du client requis.");
    if (bookingType === "scheduled" && !scheduledAt)
      return toast.error("Date et heure de la course programmée requises.");

    setLoading(true);
    try {
      const payload = {
        pickup: { address: pickup.address.trim(), coord: pickup.coord },
        dropoff: { address: dropoff.address.trim(), coord: dropoff.coord },
        stops: stops.map((s) => ({
          address: s.address.trim(),
          coord: s.coord,
        })),
        client_name: client.client_name.trim(),
        client_phone: client.client_phone.trim(),
        room_number: client.room_number.trim() || "—",
        passenger_count: Number(client.passenger_count) || 1,
        luggage_count: Number(client.luggage_count) || 0,
        notes: client.notes.trim(),
        vehicle_type: vehicleType,
        booking_type: bookingType,
        scheduled_at: bookingType === "scheduled" ? scheduledAt : null,
        receptionist_id: receptionistId || null,
      };
      const res = await api.post("/hotel/bookings/multi", payload);
      const { booking, tracking_link } = res.data;
      if (tracking_link) {
        try {
          await navigator.clipboard.writeText(tracking_link);
        } catch (_) {}
      }
      setConfirm({ booking, tracking_link });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background py-8 px-4 sm:px-6">
      <div className="mx-auto max-w-2xl">
        <div className="mb-6">
          <button
            type="button"
            onClick={() => nav("/dashboard/hotel")}
            className="text-xs uppercase tracking-widest text-muted-foreground hover:text-primary"
            data-testid="back-dashboard"
          >
            ← Retour au tableau de bord
          </button>
          <div className="mt-3 flex items-center gap-3">
            <div className="grid h-12 w-12 place-items-center rounded-2xl bg-primary/10 text-primary">
              <Route className="h-6 w-6" />
            </div>
            <div>
              <h1 className="font-[Outfit] text-3xl font-semibold tracking-tight">
                Multi-destination
              </h1>
              <p className="text-sm text-muted-foreground">
                Choisissez librement départ, arrivée et jusqu'à {MAX_STOPS}{" "}
                arrêts intermédiaires.
              </p>
            </div>
          </div>
          <HotelNavPills active="multi" />
        </div>

        <form
          onSubmit={submit}
          className="space-y-6"
          data-testid="multi-dest-form"
        >
          {/* Itinéraire */}
          <section className="rounded-3xl border border-border bg-card p-6 shadow-sm sm:p-7 space-y-4">
            <h2 className="font-[Outfit] text-lg font-medium">Itinéraire</h2>

            <FieldBlock
              icon={<PlayCircle className="h-4 w-4 text-emerald-600" />}
              label="Adresse de départ"
            >
              <AddressAutocomplete
                value={pickup.address}
                onChange={(v) => setPickup({ address: v, coord: null })}
                onSelect={(text, coord) => setPickup({ address: text, coord })}
                placeholder="Adresse de prise en charge"
                proximityCoord={profile?.coord}
                data-testid="input-pickup"
              />
            </FieldBlock>

            {stops.map((s, i) => (
              <FieldBlock
                key={i}
                icon={<MapPin className="h-4 w-4 text-primary" />}
                label={`Arrêt ${i + 1}`}
                right={
                  <button
                    type="button"
                    onClick={() => removeStop(i)}
                    className="inline-flex items-center gap-1 rounded-full border border-destructive/40 px-2 py-0.5 text-[11px] text-destructive hover:bg-destructive/10"
                    data-testid={`remove-stop-${i}`}
                  >
                    <Trash2 className="h-3 w-3" />
                    Supprimer
                  </button>
                }
              >
                <AddressAutocomplete
                  value={s.address}
                  onChange={(v) => updateStop(i, { address: v, coord: null })}
                  onSelect={(text, coord) =>
                    updateStop(i, { address: text, coord })
                  }
                  placeholder={`Adresse de l'arrêt ${i + 1}`}
                  proximityCoord={profile?.coord}
                  data-testid={`input-stop-${i}`}
                />
              </FieldBlock>
            ))}

            <div className="flex justify-center -my-2">
              <ArrowDown className="h-4 w-4 text-muted-foreground/60" />
            </div>

            <FieldBlock
              icon={<Flag className="h-4 w-4 text-red-500" />}
              label="Adresse d'arrivée"
            >
              <AddressAutocomplete
                value={dropoff.address}
                onChange={(v) => setDropoff({ address: v, coord: null })}
                onSelect={(text, coord) => setDropoff({ address: text, coord })}
                placeholder="Destination finale"
                proximityCoord={profile?.coord}
                data-testid="input-dropoff"
              />
            </FieldBlock>

            <Button
              type="button"
              variant="outline"
              onClick={addStop}
              disabled={stops.length >= MAX_STOPS}
              className="w-full rounded-full gap-1.5"
              data-testid="add-stop"
            >
              <Plus className="h-4 w-4" />
              Ajouter un arrêt{" "}
              <span className="text-muted-foreground">
                ({stops.length}/{MAX_STOPS})
              </span>
            </Button>
          </section>

          {/* Client */}
          <section className="rounded-3xl border border-border bg-card p-6 shadow-sm sm:p-7 space-y-4">
            <h2 className="font-[Outfit] text-lg font-medium">Client</h2>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Nom du client">
                <Input
                  value={client.client_name}
                  onChange={(e) =>
                    setClient((c) => ({ ...c, client_name: e.target.value }))
                  }
                  placeholder="Jean Dupont"
                  required
                  data-testid="input-client-name"
                />
              </Field>
              <Field label="Chambre / N°">
                <Input
                  value={client.room_number}
                  onChange={(e) =>
                    setClient((c) => ({ ...c, room_number: e.target.value }))
                  }
                  placeholder="—"
                  data-testid="input-room"
                />
              </Field>
            </div>

            <Field label="Téléphone du client (facultatif)">
              <PhoneInput
                value={client.client_phone}
                onChange={(v) =>
                  setClient((c) => ({ ...c, client_phone: v }))
                }
                data-testid="input-client-phone"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Passagers" icon={<Users className="h-3.5 w-3.5" />}>
                <Input
                  type="number"
                  min="1"
                  max="20"
                  value={client.passenger_count}
                  onChange={(e) =>
                    setClient((c) => ({
                      ...c,
                      passenger_count: e.target.value,
                    }))
                  }
                  data-testid="input-passengers"
                />
              </Field>
              <Field
                label="Bagages"
                icon={<Briefcase className="h-3.5 w-3.5" />}
              >
                <Input
                  type="number"
                  min="0"
                  max="20"
                  value={client.luggage_count}
                  onChange={(e) =>
                    setClient((c) => ({
                      ...c,
                      luggage_count: e.target.value,
                    }))
                  }
                  data-testid="input-luggage"
                />
              </Field>
            </div>

            <Field label="Notes (facultatif)">
              <Textarea
                value={client.notes}
                onChange={(e) =>
                  setClient((c) => ({ ...c, notes: e.target.value }))
                }
                placeholder="Demandes particulières, ordre des arrêts à respecter…"
                rows={2}
                data-testid="input-notes"
              />
            </Field>
          </section>

          {/* Course */}
          <section className="rounded-3xl border border-border bg-card p-6 shadow-sm sm:p-7 space-y-4">
            <h2 className="font-[Outfit] text-lg font-medium">
              Type de course
            </h2>

            <div className="grid grid-cols-2 gap-2">
              <PillButton
                active={bookingType === "immediate"}
                onClick={() => setBookingType("immediate")}
                icon={<Sparkles className="h-4 w-4" />}
                label="Immédiate"
                testid="type-immediate"
              />
              <PillButton
                active={bookingType === "scheduled"}
                onClick={() => setBookingType("scheduled")}
                icon={<Clock className="h-4 w-4" />}
                label="Programmée"
                testid="type-scheduled"
              />
            </div>

            {bookingType === "scheduled" && (
              <Field label="Date et heure">
                <Input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  required
                  data-testid="input-scheduled-at"
                />
              </Field>
            )}

            <Field label="Type de véhicule">
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: "sedan", label: "Berline" },
                  { key: "premium_sedan", label: "Premium" },
                  { key: "van", label: "Van" },
                ].map((v) => (
                  <PillButton
                    key={v.key}
                    active={vehicleType === v.key}
                    onClick={() => setVehicleType(v.key)}
                    label={v.label}
                    testid={`vehicle-${v.key}`}
                  />
                ))}
              </div>
            </Field>

            {receptionists.length > 0 && (
              <Field label="Réceptionniste en service (facultatif)">
                <div className="flex flex-wrap items-center gap-2">
                  <ReceptionistPill
                    active={!receptionistId}
                    onClick={() => setReceptionistId("")}
                    label="Aucun"
                    testid="recept-none"
                  />
                  {receptionists.map((r) => (
                    <ReceptionistPill
                      key={r.id}
                      active={receptionistId === r.id}
                      onClick={() => setReceptionistId(r.id)}
                      label={r.name}
                      testid={`recept-${r.id}`}
                    />
                  ))}
                </div>
              </Field>
            )}
          </section>

          <Button
            type="submit"
            size="lg"
            className="w-full rounded-full gap-2"
            disabled={loading}
            data-testid="submit-multi-booking"
          >
            <Route className="h-4 w-4" />
            {loading
              ? "Création..."
              : `Créer la course ${stops.length > 0 ? `(${stops.length + 1} étape${stops.length + 1 > 1 ? "s" : ""})` : ""}`}
          </Button>

          <p className="text-center text-[11px] text-muted-foreground">
            Le prix est calculé automatiquement selon la distance totale
            (départ + arrêts + arrivée) via Mapbox.
          </p>
        </form>
      </div>

      <Dialog
        open={!!confirm}
        onOpenChange={(o) => {
          if (!o) {
            setConfirm(null);
            nav("/dashboard/hotel/history");
          }
        }}
      >
        <DialogContent
          data-testid="multi-booking-confirm-dialog"
          className="overflow-hidden"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Course multi-destination réservée</DialogTitle>
            <DialogDescription>
              Votre demande a bien été enregistrée.
            </DialogDescription>
          </DialogHeader>
          {confirm && (
            <div className="flex flex-col items-center py-6 text-center">
              <div className="relative animate-in zoom-in-50 fade-in duration-500">
                <div className="relative grid h-20 w-20 place-items-center rounded-full bg-primary shadow-lg">
                  <span className="font-[Outfit] text-4xl font-light text-primary-foreground">
                    S
                  </span>
                  <span className="absolute -right-1 -bottom-1 h-4 w-4 rounded-full bg-accent ring-4 ring-card" />
                </div>
                <span
                  className="absolute inset-0 -z-10 animate-ping-slow rounded-full bg-primary/20"
                  aria-hidden="true"
                />
              </div>

              <h2 className="mt-5 font-[Outfit] text-3xl font-normal tracking-tight animate-in slide-in-from-bottom-2 fade-in duration-500 delay-100">
                Saor
              </h2>

              <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/10 px-4 py-1.5 text-sm font-medium text-[hsl(var(--success))] animate-in slide-in-from-bottom-2 fade-in duration-500 delay-200">
                <Check className="h-4 w-4" /> Course multi-arrêts réservée
              </p>

              <p className="mt-3 max-w-[280px] text-sm text-muted-foreground animate-in fade-in duration-700 delay-300">
                Un chauffeur sera notifié et vous verrez son ETA en temps réel
                dès qu'il accepte. Le lien de suivi a été copié.
              </p>

              <div
                className="mt-6 flex w-full flex-col items-center gap-1 rounded-xl border border-border bg-secondary/30 px-4 py-3 animate-in fade-in duration-700 delay-300"
                data-testid="multi-confirm-summary"
              >
                <div className="inline-flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Prix estimé</span>
                  <span
                    className="font-[Outfit] text-xl font-semibold text-primary"
                    data-testid="multi-confirm-price"
                  >
                    {confirm.booking.price} €
                  </span>
                  <span className="text-xs text-muted-foreground">
                    · {confirm.booking.vehicle_type_label}
                  </span>
                </div>
                <div className="inline-flex items-center gap-3 text-[11px] text-muted-foreground">
                  <span>
                    <Route className="inline h-3 w-3 mr-0.5" />
                    {confirm.booking.distance_km} km
                  </span>
                  <span>·</span>
                  <span>
                    <MapPin className="inline h-3 w-3 mr-0.5" />
                    {(confirm.booking.stops || []).length} arrêt(s)
                    intermédiaire(s)
                  </span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, icon, children }) {
  return (
    <Label className="space-y-1.5 block">
      <span className="text-sm text-foreground inline-flex items-center gap-1.5">
        {icon}
        {label}
      </span>
      {children}
    </Label>
  );
}

function FieldBlock({ icon, label, right, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground">
          {icon}
          {label}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function PillButton({ active, onClick, icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center justify-center gap-1.5 rounded-full border px-3 py-2 text-sm transition-colors ${
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-card text-foreground/70 hover:border-primary/40"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function ReceptionistPill({ active, onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-card text-foreground/70 hover:border-primary/40"
      }`}
    >
      {label}
    </button>
  );
}
